import React, { useEffect, useRef, useState } from 'react';
import {
  getNumOfCol,
  getPreparedFlats,
} from '../../../../../../../HousingStock/components/ui/FlatsGrid/utils';
import GridContainer from '../GridContainer/GridContainer';
import { gridContainerTypes } from '../../../../../../../HousingStock/components/ui/FlatsGrid/constants';
import useInView from '../../../../../../../HousingStock/hooks/useInView';
import FloorTooltip from '../../../../../../../HousingStock/components/ui/FlatsGrid/components/FloorTooltip/FloorTooltip';

const initialCustomTooltipData = {
  visible: false,
  content: '',
  height: '',
  top: '',
  left: 0,
};

const FlatsGrid = ({
  data,
  className,
  setCurrentEntry,
  colSize = '46px',
  rowSize = '46px',
  onFlatClick,
  parentContainer,
  threshold = 0.9,
  rootMargin = '100% 0px 100% 0px',
  skip = false,
  activeFlat,
  activeEntranceName,
  entrancesInView,
  setEntrancesInView,
}) => {
  const [upperRooms, setUpperRooms] = useState([]);
  const [basementRooms, setBasementRooms] = useState([]);
  const [undergroundRooms, setUndergroundRooms] = useState([]);
  const [numOfCol, setNumOfCol] = useState(1);
  const [customTooltipData, setCustomTooltipData] = useState(
    initialCustomTooltipData
  );

  const ref = useRef();

  const handleMouseOver = (e) => {
    const target = e.target;

    if (target.hasAttribute('data-floor')) {
      const data = {
        visible: true,
        content: target.getAttribute('data-floor'),
        height: target.offsetHeight + 8,
        top: target.offsetTop - 4,
        left: ref.current.getBoundingClientRect().left,
      };

      setCustomTooltipData(data);
    }
  };

  const handleMouseOut = (e) => {
    setCustomTooltipData(initialCustomTooltipData);
  };

  const {
    ref: inViewRef,
    inView,
    entry,
  } = useInView({
    root: parentContainer?.current,
    threshold,
    rootMargin,
    skip,
  });

  useEffect(() => {
    let upper = [];
    let basement = [];
    let under = [];
    if (data?.flats) {
      const { upperRooms, basementRooms, undergroundRooms } = getPreparedFlats(
        data.flats
      );

      upper = upperRooms;
      basement = basementRooms;
      under = undergroundRooms;
    }

    setUpperRooms(upper);
    setBasementRooms(basement);
    setUndergroundRooms(under);
  }, [data]);

  useEffect(() => {
    let colCount = 1;

    if (data.flats?.length > 0) {
      colCount = getNumOfCol(data.flats);
    }

    setNumOfCol(colCount);
  }, [data.flats]);

  useEffect(() => {
    let newEntrancesInView;

    if (inView) {
      newEntrancesInView = [...entrancesInView, data.name];
    } else {
      newEntrancesInView = entrancesInView.filter(
        (entranceId) => entranceId !== data.name
      );
    }
    setEntrancesInView(newEntrancesInView);
  }, [inView, data.name]);

  useEffect(() => {
    if (setCurrentEntry && activeEntranceName === data.name && entry) {
      setCurrentEntry(entry.target);
    }
  }, [activeEntranceName, data.name, entry, setCurrentEntry]);

  return (
    <div
      ref={inViewRef}
      className={className}
    >
      <div
        ref={ref}
        style={{ position: 'relative' }}
      >
        {customTooltipData.visible && <FloorTooltip data={customTooltipData} />}
        {Boolean(upperRooms.length) && (
          <GridContainer
            rowSize={rowSize}
            numOfCol={numOfCol}
            flats={upperRooms}
            onFlatClick={onFlatClick}
            colSize={colSize}
            title={data.name}
            activeFlat={activeFlat}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          />
        )}
        {Boolean(basementRooms.length) && (
          <GridContainer
            rowSize={rowSize}
            numOfCol={numOfCol}
            flats={basementRooms}
            onFlatClick={onFlatClick}
            colSize={colSize}
            type={gridContainerTypes.basement}
            activeFlat={activeFlat}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          />
        )}
        {Boolean(undergroundRooms.length) && (
          <GridContainer
            rowSize={rowSize}
            numOfCol={numOfCol}
            flats={undergroundRooms}
            onFlatClick={onFlatClick}
            colSize={colSize}
            type={gridContainerTypes.under}
            activeFlat={activeFlat}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          />
        )}
      </div>
    </div>
  );
};

export default FlatsGrid;
